/* ===========================================================================
   BB CARD DRAFT FIX — keeps what the customer is typing in the card form
   ---------------------------------------------------------------------------
   Two bugs on step 3 (Pick a Card), both from the same cause: the builder
   only remembered the card message once "Save Message" was clicked, and it
   redraws the form from that saved copy whenever its state changes.

     1. Typing a message, then picking a different card design, wiped
        To / From / Message (nothing was saved yet, so the redraw was empty).
     2. After saving, the first character typed in any field was undone
        (that keystroke marks the card "unsaved", which redraws the form
        from the saved copy — without the new character).

   The fix: on every keystroke in the card form, copy the current To / From /
   Message into the builder's stored card BEFORE the builder's own input
   handler runs (capture phase on document). The redraw then fills in exactly
   what is on screen, and switching designs carries the text over.

   Nothing else changes. The card is still only "saved" (Save Message, or
   Continue, which saves automatically) under the existing rules; the draft
   just stops getting thrown away. Cart properties are unaffected because
   Continue on step 3 still runs the normal save before the cart is built.

   AUTOSAVE (added 2026-10-23, Moshe). Step 3 no longer has a "Save Message"
   button (hidden in bb-qa-overrides.css). Continue already saved the card
   when clicked, but it looked greyed out until Save was pressed, so people
   thought they were stuck. Now:
     - the message is saved when the shopper leaves a field (and on Continue);
     - Continue lights up as soon as From and Message are filled, or "Leave
       Card Blank" is ticked;
     - a small "Message saved" line confirms it under the message box;
     - pressing Continue too early still shows the builder's own "finish your
       card" prompt, and the empty required fields get a red outline.

   Self-contained override, same pattern as bb-sizing-aplus.js: removing its
   <script> line in snippets/bb-assets.liquid fully reverts it. Long term this
   belongs in box-builder.js (BBStore.setCard / markCardUnsaved / fillForm).
   Added 2026-10-22.
   =========================================================================== */
(function () {
  "use strict";
  if (window.__bbCardDraftFix) return;
  window.__bbCardDraftFix = true;

  var STORAGE_KEY = "bb_box_v1";
  var FIELDS = {
    "data-bb-card-to": "To",
    "data-bb-card-from": "From",
    "data-bb-card-message": "Message"
  };

  function fieldValue(attr) {
    var el = document.querySelector("[" + attr + "]");
    return el ? String(el.value || "") : "";
  }

  function rememberDraft() {
    var raw;
    try {
      raw = sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return;
    }
    if (!raw) return;
    var state;
    try {
      state = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!state || !state.card || !state.card.variantId || state.card.blank || state.skipCard) return;

    var props = {};
    Object.keys(FIELDS).forEach(function (attr) {
      var value = fieldValue(attr);
      if (value) props[FIELDS[attr]] = value;
    });
    state.card.properties = props;

    /* Written straight to storage on purpose — no "bb:state" event — so this
       does not trigger a redraw of its own. The builder's own handler, which
       runs right after this one, does any redraw, and it now reads these
       values. */
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* ignore quota */ }
  }

  document.addEventListener(
    "input",
    function (event) {
      var target = event.target;
      if (!target || !target.hasAttribute) return;
      for (var attr in FIELDS) {
        if (target.hasAttribute(attr)) {
          rememberDraft();
          return;
        }
      }
    },
    true
  );

  /* ---- Autosave (2026-10-23) ------------------------------------------ */
  function el(attr) {
    return document.querySelector("[" + attr + "]");
  }
  function val(attr) {
    var node = el(attr);
    return node ? String(node.value || "") : "";
  }
  function isBlank() {
    var b = el("data-bb-card-blank");
    return !!(b && b.checked);
  }
  function cardComplete() {
    if (isBlank()) return true;
    return val("data-bb-card-from").trim().length > 0 && val("data-bb-card-message").trim().length > 0;
  }
  function onCardStep() {
    var s = window.BBStore && window.BBStore.get && window.BBStore.get();
    return !!(s && Number(s.step) === 3 && !s.skipCard && s.card && s.card.variantId);
  }
  function statusLine() {
    var line = el("data-bb-card-autosave");
    if (line) return line;
    var save = el("data-bb-card-save");
    if (!save || !save.parentNode) return null;
    line = document.createElement("p");
    line.className = "bb-card-form__autosave";
    line.setAttribute("data-bb-card-autosave", "");
    line.setAttribute("aria-live", "polite");
    line.hidden = true;
    line.innerHTML = '<svg viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 6.2 5 8.5l4.5-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Message saved</span>';
    save.parentNode.insertBefore(line, save.nextSibling);
    return line;
  }
  function clearMissing() {
    ["data-bb-card-from", "data-bb-card-message"].forEach(function (attr) {
      var node = el(attr);
      if (node && node.value.trim()) node.classList.remove("is-missing");
    });
  }
  function syncAutosave() {
    var ok = cardComplete();
    var line = statusLine();
    if (line) line.hidden = !(ok && !isBlank());
    if (!onCardStep()) return;
    document.querySelectorAll("[data-bb-continue]").forEach(function (btn) {
      btn.classList.toggle("is-disabled", !ok);
      btn.setAttribute("aria-disabled", ok ? "false" : "true");
    });
  }
  function saveNow() {
    if (!onCardStep() || isBlank() || !window.BBStore.saveCardNote) return;
    window.BBStore.saveCardNote({
      blank: false,
      to: val("data-bb-card-to"),
      from: val("data-bb-card-from"),
      message: val("data-bb-card-message")
    });
  }

  /* Live: Continue and the "saved" line follow what's typed. */
  document.addEventListener("input", function (event) {
    var t = event.target;
    if (!t || !t.hasAttribute) return;
    for (var attr in FIELDS) {
      if (t.hasAttribute(attr)) {
        clearMissing();
        syncAutosave();
        return;
      }
    }
  });

  /* Leaving a field saves it, so the Box Summary and the step bar see it. */
  document.addEventListener("change", function (event) {
    var t = event.target;
    if (!t || !t.hasAttribute) return;
    if (t.hasAttribute("data-bb-card-blank")) {
      syncAutosave();
      return;
    }
    for (var attr in FIELDS) {
      if (t.hasAttribute(attr)) {
        saveNow();
        syncAutosave();
        return;
      }
    }
  });

  /* Continue pressed too early: outline the empty required fields. The
     builder's own handler shows its "finish your card" prompt. */
  document.addEventListener(
    "click",
    function (event) {
      if (!event.target.closest || !event.target.closest("[data-bb-continue]")) return;
      if (!onCardStep() || cardComplete()) return;
      ["data-bb-card-from", "data-bb-card-message"].forEach(function (attr) {
        var node = el(attr);
        if (node && !node.value.trim()) node.classList.add("is-missing");
      });
    },
    true
  );

  /* The builder redraws Continue on every state change; reapply after. */
  document.addEventListener("bb:shell-render", syncAutosave);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAutosave);
  } else {
    syncAutosave();
  }
})();
